import React, { useEffect, useRef, memo } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import './StateVillageLabel.css';

const StateVillageLabel = memo(({ 
  stateGeoData, 
  villageDataByState, // { "Tamil Nadu": { count: 1200, avgWaterLevel: 6.5, critical: 180, safe: 800 } }
  onStateClick 
}) => {
  const map = useMap();
  const labelsLayerRef = useRef(null);

  useEffect(() => {
    // Clear existing labels
    if (labelsLayerRef.current) {
      map.removeLayer(labelsLayerRef.current);
      labelsLayerRef.current = null;
    }

    if (!stateGeoData || !villageDataByState || Object.keys(villageDataByState).length === 0) {
      return;
    }

    const labelsLayer = L.layerGroup();

    // Use neutral color for all badges (no water level classification)
    const badgeColor = '#2196F3'; // Blue - neutral color

    // Helper to normalize state names for matching
    const normalizeStateName = (name) => {
      if (!name) return '';
      return name.toUpperCase().trim().replace(/\s+/g, '');
    };

    // Process each state feature
    stateGeoData.features.forEach(feature => {
      const stateName = feature.properties.st_nm || feature.properties.NAME_1;
      if (!stateName) return;

      const normalizedName = normalizeStateName(stateName);
      
      // Find matching village data
      const villageData = Object.keys(villageDataByState).find(key => 
        normalizeStateName(key) === normalizedName
      );

      if (!villageData) return;

      const stats = villageDataByState[villageData];
      const color = badgeColor;

      // Calculate center of state polygon
      let center;
      if (feature.geometry.type === 'Polygon') {
        const coords = feature.geometry.coordinates[0];
        const lats = coords.map(c => c[1]);
        const lngs = coords.map(c => c[0]);
        center = [
          (Math.min(...lats) + Math.max(...lats)) / 2,
          (Math.min(...lngs) + Math.max(...lngs)) / 2
        ];
      } else if (feature.geometry.type === 'MultiPolygon') {
        // Use first polygon's center
        const coords = feature.geometry.coordinates[0][0];
        const lats = coords.map(c => c[1]);
        const lngs = coords.map(c => c[0]);
        center = [
          (Math.min(...lats) + Math.max(...lats)) / 2,
          (Math.min(...lngs) + Math.max(...lngs)) / 2
        ];
      } else {
        return;
      }

      // Create badge with village count
      const icon = L.divIcon({
        className: 'state-village-label',
        html: `
          <div class="state-badge" style="background-color: ${color};">
            <div class="badge-count">${stats.count.toLocaleString()}</div>
            <div class="badge-label">villages</div>
            <div class="badge-state">${stateName}</div>
          </div>
        `,
        iconSize: [80, 80],
        iconAnchor: [40, 40]
      });

      const marker = L.marker(center, { 
        icon,
        zIndexOffset: 1000
      });

      // Click handler
      marker.on('click', () => {
        if (onStateClick) {
          onStateClick(stateName, center);
        }
      });

      // Hover effect
      marker.on('mouseover', function() {
        const element = this.getElement();
        if (element) {
          element.classList.add('hovered');
        }
      });

      marker.on('mouseout', function() {
        const element = this.getElement();
        if (element) {
          element.classList.remove('hovered');
        }
      });

      labelsLayer.addLayer(marker);
    });

    // Add to map
    labelsLayer.addTo(map);
    labelsLayerRef.current = labelsLayer;

    // Cleanup
    return () => {
      if (labelsLayerRef.current) {
        map.removeLayer(labelsLayerRef.current);
      }
    };
  }, [stateGeoData, villageDataByState, map, onStateClick]);

  return null;
});

StateVillageLabel.displayName = 'StateVillageLabel';

export default StateVillageLabel;
